/**
 * Client-side board ACL (must match backend BoardService rowHasViewerAccess / rowHasOwnerAccess).
 * Used to filter board lists so UI never shows boards the API should not return (defense in depth).
 */

function getPermissionsObject(board) {
  const raw = board?.permissions;
  if (raw == null) return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s === "" || s === "{}") return {};
    try {
      const o = JSON.parse(s);
      return typeof o === "object" && o !== null && !Array.isArray(o) ? o : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  return null;
}

function rowIsLegacyPermissions(board) {
  const raw = board?.permissions;
  if (raw == null) return true;
  const obj = getPermissionsObject(board);
  if (obj === null) return true;
  return Object.keys(obj).length === 0;
}

function getBoardPermissions(board) {
  const obj = getPermissionsObject(board);
  const p = obj && typeof obj === "object" ? obj : {};
  const editors = Array.isArray(p.editors)
    ? p.editors.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  const viewers = Array.isArray(p.viewers)
    ? p.viewers.map((x) => Number(x)).filter((n) => Number.isFinite(n))
    : [];
  const owner = Number(p.owner ?? board?.userId);
  return { editors, viewers, owner: Number.isFinite(owner) ? owner : null };
}

function rowHasOwnerAccessBoard(board, userIdInt) {
  if (!Number.isInteger(userIdInt)) return false;
  const boardUid = Number(board?.userId);
  if (Number.isFinite(boardUid) && boardUid === userIdInt) return true;
  const p = getPermissionsObject(board);
  const obj = p && typeof p === "object" ? p : {};
  const po = Number(obj.owner);
  if (Number.isFinite(po) && po === userIdInt) return true;
  return false;
}

/**
 * Whether the signed-in user may see this board in lists / open it (viewer or higher).
 */
/**
 * Whether the user may add or edit cards on this board (owner or listed editor; not viewer-only).
 */
export function userCanEditBoard(board, userId) {
  if (!board || userId == null || userId === "") return false;
  const uid =
    typeof userId === "number" && Number.isInteger(userId)
      ? userId
      : parseInt(String(userId).trim(), 10);
  if (!Number.isInteger(uid) || uid < 1) return false;

  if (rowIsLegacyPermissions(board)) {
    return rowHasOwnerAccessBoard(board, uid);
  }
  if (rowHasOwnerAccessBoard(board, uid)) return true;
  const { editors } = getBoardPermissions(board);
  return editors.includes(uid);
}

export function userCanViewBoard(board, userId) {
  if (!board || userId == null || userId === "") return false;
  const uid =
    typeof userId === "number" && Number.isInteger(userId)
      ? userId
      : parseInt(String(userId).trim(), 10);
  if (!Number.isInteger(uid) || uid < 1) return false;

  if (rowIsLegacyPermissions(board)) {
    return rowHasOwnerAccessBoard(board, uid);
  }
  if (rowHasOwnerAccessBoard(board, uid)) return true;
  const { editors, viewers } = getBoardPermissions(board);
  return editors.includes(uid) || viewers.includes(uid);
}

export function getStoredUserId() {
  try {
    const raw = localStorage.getItem("User");
    if (!raw) return null;
    const u = JSON.parse(raw);
    const id = u.id ?? u._id;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}
